import { zodResolver } from '@hookform/resolvers/zod';
import { O } from '@mobily/ts-belt';
import { Button, CircularProgress, Grid, Stack, TextField, Typography } from '@mui/material';
import { useErrorMessage } from 'app/ui/validationError/useErrorMessage';
import { useTranslation } from 'common/hooks/useTranslation';
import { Lambda } from 'common/types';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { usePropertyContext } from '../../../properties/components/SingleProperty';
import { useDeleteCombiner } from '../../hooks/useDelete';
import { usePatchCombiner } from '../../hooks/usePatch';
import { CombinerDetails } from '../../types/ObjectDetails';
import { combinerSchema, CombinerSchema } from '../../types/CombinerSchema';
import { PhotosListWithUpload } from '../PhotosListWithUpload';

type Props = {
  details: CombinerDetails;
  onSave: Lambda<void, void>;
  onDelete?: Lambda<void, void>;
};

export const Combiner: FC<Props> = ({ details, onSave, onDelete }) => {
  const { t } = useTranslation();
  const { id: propertyId } = usePropertyContext();
  const { location, text, id, photosURLs, leftPortCount, rightPortCount, unitType, serialNumber } = details;
  const { mutate: patchCombiner, isLoading: isLoadingPatchCombiner } = usePatchCombiner({
    onSuccess: () => {
      onSave();
      toast.success(t('general.updatedSuccessfully'));
    },
    onError: () => {
      toast.error(t('error.generalMessage'));
    },
  });
  const { mutate: deleteCombiner, isLoading: isLoadingDeleteCombiner } = useDeleteCombiner({
    onSuccess: () => {
      if (onDelete) onDelete();
      toast.success(t('general.deletedSuccessfully'));
    },
    onError: () => {
      toast.error(t('error.generalMessage'));
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<CombinerSchema>({
    defaultValues: {
      text: O.getWithDefault(text, ''),
      location: O.getWithDefault(location, ''),
      leftPortCount: O.getWithDefault(leftPortCount, 4),
      rightPortCount: O.getWithDefault(rightPortCount, 4),
      unitType: O.getWithDefault(unitType, ''),
      serialNumber: O.getWithDefault(serialNumber, ''),
    },
    mode: 'onChange',
    resolver: zodResolver(combinerSchema),
  });

  const [photosUrls, setPhotosUrls] = useState(photosURLs ?? []);

  const getErrorMessage = useErrorMessage(errors);

  const handleSave = (values: CombinerSchema) => {
    patchCombiner({ ...values, id, photosURLs: photosUrls });
  };

  const handleDelete = () => {
    deleteCombiner(details.id);
  };

  return (
    <Stack component="form" onSubmit={handleSubmit(handleSave)}>
      <Typography sx={{ fontSize: 18, paddingBottom: 2 }}>{t('areas.combiner.label')}</Typography>
      <Stack gap={2} width="100%">
        <TextField label={t('areas.fields.name')} {...register('text')} helperText={getErrorMessage('text')} />
        <TextField
          label={t('areas.fields.leftPortCount')}
          {...register('leftPortCount', {
            valueAsNumber: true,
          })}
          type="number"
          helperText={getErrorMessage('leftPortCount')}
        />
        <TextField
          label={t('areas.fields.rightPortCount')}
          {...register('rightPortCount', {
            valueAsNumber: true,
          })}
          type="number"
          helperText={getErrorMessage('rightPortCount')}
        />
        <PhotosListWithUpload
          photosURLs={photosUrls}
          setPhotosUrls={setPhotosUrls}
          entityType="combiner"
          entityId={id}
          propertyId={O.getWithDefault(propertyId, 0)}
        />
        <TextField
          label={t('areas.fields.location')}
          {...register('location')}
          helperText={getErrorMessage('location')}
        />
        <TextField
          label={t('areas.fields.unitType')}
          {...register('unitType')}
          helperText={getErrorMessage('unitType')}
        />
        <TextField
          label={t('areas.fields.serialNumber')}
          {...register('serialNumber')}
          helperText={getErrorMessage('serialNumber')}
        />
      </Stack>
      <Grid container justifyContent="space-between" mt={2}>
        <Button variant="text" color="error" onClick={handleDelete} disabled={isLoadingDeleteCombiner}>
          {isLoadingDeleteCombiner ? <CircularProgress size={16} /> : t('general.delete')}
        </Button>
        <Button variant="contained" color="primary" type="submit" disabled={!isValid || isLoadingPatchCombiner}>
          {isLoadingPatchCombiner ? <CircularProgress size={16} /> : t('general.save')}
        </Button>
      </Grid>
    </Stack>
  );
};
